import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Pages
import HomePage from "./pages/HomePage";
import SearchResultsPage from "./pages/SearchResultsPage";
import CarDetailsPage from "./pages/CarDetailsPage";
import CheckoutPage from "./pages/CheckoutPage";
import TripsPage from "./pages/TripsPage";
import TripDetailsPage from "./pages/TripDetailsPage";
import ProfilePage from "./pages/ProfilePage";
import InboxPage from "./pages/InboxPage";
import ConversationPage from "./pages/ConversationPage";
import HostDashboardPage from "./pages/HostDashboardPage";
import HostListingsPage from "./pages/HostListingsPage";
import HostCalendarPage from "./pages/HostCalendarPage";
import HostAnalyticsPage from "./pages/HostAnalyticsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ComoFuncionaPage from "./pages/ComoFuncionaPage";
import SobreNosotrosPage from "./pages/SobreNosotrosPage";
import TerminosPage from "./pages/TerminosPage";
import ElegibilidadPage from "./pages/ElegibilidadPage";
import AvisoPrivacidadPage from "./pages/AvisoPrivacidadPage";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import CancelacionPage from "./pages/CancelacionPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import SecurityPage from "./pages/SecurityPage";
import GuiaPage from "./pages/GuiaPage";
import GuiaBusinessDetailPage from "./pages/GuiaBusinessDetailPage";
import GuiaCouponsPage from "./pages/GuiaCouponsPage";
import GuiaRedeemPage from "./pages/GuiaRedeemPage";
import GuiaMapPage from "./pages/GuiaMapPage";
import GuiaBusinessDashboardPage from "./pages/GuiaBusinessDashboardPage";
import LanguageSwitchedPage from "./pages/LanguageSwitchedPage";
import GuiaRegisterBusinessPage from "./pages/GuiaRegisterBusinessPage";
import PreguntasFrecuentesPage from "./pages/PreguntasFrecuentesPage";
import RequisitosHostPage from "./pages/RequisitosHostPage";
import CarrerasPage from "./pages/CarrerasPage";
import PrensaPage from "./pages/PrensaPage";
import ContactoPage from "./pages/ContactoPage";
import CalculadoraGananciasPage from "./pages/CalculadoraGananciasPage";
import CentroRecursosPage from "./pages/CentroRecursosPage";
import PlanesProteccionPage from "./pages/PlanesProteccionPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import HostInsurancePage from "./pages/HostInsurancePage";
import HostPayoutsPage from "./pages/HostPayoutsPage";
import ComingSoonBanner from "./components/ComingSoonBanner";
import ProfilePhotoGate from "./components/ProfilePhotoGate";
import AddToHomeScreenPrompt from "./components/AddToHomeScreenPrompt";
import EmailVerificationBanner from "./components/EmailVerificationBanner";

// Components
import Navbar from "./components/Navbar";
import ReviewerDemoBanner from "./components/ReviewerDemoBanner";
import PageBackNav from "./components/PageBackNav";
import { PwaInstallProvider } from "./contexts/PwaInstallContext";
import { CompareProvider } from "./contexts/CompareContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PwaInstallSurfaces } from "./components/PwaInstall";
import CompareVehiclesPage from "./pages/CompareVehiclesPage";
import Footer from "./components/Footer";
import AIChatWidget from "./components/AIChatWidget";
import BottomNav from "./components/BottomNav";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import usePageViewTracker from "./hooks/usePageViewTracker";

import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// localStorage key for the session token fallback (iOS Safari ITP blocks
// cross-site cookies, so we mirror the token here and send it as Bearer).
const SESSION_TOKEN_KEY = "rivones_session_token";
// Same iOS-ITP workaround for the "Confiar en este dispositivo 30 días" cookie.
// We mirror the signed token into localStorage on 2FA verify and replay it as
// `X-Rivones-Trusted-Device` on the next /auth/login call. The token is HMAC
// signed server-side so it's just as authoritative as the cookie.
const TRUSTED_DEVICE_TOKEN_KEY = "rivones_td_token";

export const getStoredSessionToken = () => {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch (_e) {
    return null;
  }
};

export const setStoredSessionToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
  } catch (_e) {
    /* localStorage disabled (private mode) — token survives only in memory */
  }
};

export const getStoredTrustedDeviceToken = () => {
  try {
    return localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
  } catch (_e) {
    return null;
  }
};

export const setStoredTrustedDeviceToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
    }
  } catch (_e) {
    /* skip */
  }
};

// Create axios instance
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// REQUEST interceptor: attach Bearer token from localStorage (iOS Safari
// fallback when 3rd-party cookies are blocked by ITP). Also attaches the
// trusted-device token as `X-Rivones-Trusted-Device` on login so we can
// skip OTP for previously-confirmed devices even when ITP drops the cookie.
api.interceptors.request.use((config) => {
  const token = getStoredSessionToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  const tdToken = getStoredTrustedDeviceToken();
  if (tdToken) {
    config.headers = config.headers || {};
    if (!config.headers["X-Rivones-Trusted-Device"]) {
      config.headers["X-Rivones-Trusted-Device"] = tdToken;
    }
  }
  return config;
});

// RESPONSE interceptor: persist session_token + trusted_device_token if the
// auth endpoints return them in the JSON body.
api.interceptors.response.use(
  (response) => {
    const token = response?.data?.session_token;
    if (token && typeof token === "string") {
      setStoredSessionToken(token);
    }
    const tdToken = response?.data?.trusted_device_token;
    if (tdToken && typeof tdToken === "string") {
      setStoredTrustedDeviceToken(tdToken);
    }
    return response;
  },
  (error) => {
    // If the server tells us the session is gone, wipe the local token so the
    // next /auth/me check doesn't keep hitting 401 with a stale Bearer.
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && url.includes("/auth/me")) {
      setStoredSessionToken(null);
    }
    return Promise.reject(error);
  },
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setStoredSessionToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles.length > 0 && !requiredRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  usePageViewTracker();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col h-screen-safe">
      <ScrollToTop />
      <ComingSoonBanner />
      <ReviewerDemoBanner />
      <EmailVerificationBanner />
      <Navbar />
      <PageBackNav />
      <main className="flex-1 page-wrap">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/comparar" element={<CompareVehiclesPage />} />
          <Route path="/cars/:vehicleId" element={<CarDetailsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />
          <Route path="/sobre-nosotros" element={<SobreNosotrosPage />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/elegibilidad" element={<ElegibilidadPage />} />
          <Route path="/privacidad" element={<AvisoPrivacidadPage />} />
          <Route path="/eliminar-cuenta" element={<AccountDeletionPage />} />
          <Route path="/cancelacion" element={<CancelacionPage />} />
          <Route path="/idioma-cambiado" element={<LanguageSwitchedPage />} />
          <Route path="/u/:userId" element={<PublicProfilePage />} />
          <Route path="/guia" element={<GuiaPage />} />
          <Route path="/guia/registrar" element={<GuiaRegisterBusinessPage />} />
          <Route path="/guia/mapa" element={<GuiaMapPage />} />
          <Route
            path="/guia/mis-cupones"
            element={
              <ProtectedRoute>
                <GuiaCouponsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guia/redeem"
            element={
              <ProtectedRoute>
                <GuiaRedeemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guia/:businessId/dashboard"
            element={
              <ProtectedRoute>
                <GuiaBusinessDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route path="/guia/:businessId" element={<GuiaBusinessDetailPage />} />
          <Route path="/preguntas-frecuentes" element={<PreguntasFrecuentesPage />} />
          <Route path="/requisitos-host" element={<RequisitosHostPage />} />
          <Route path="/carreras" element={<CarrerasPage />} />
          <Route path="/prensa" element={<PrensaPage />} />
          <Route path="/contacto" element={<ContactoPage />} />
          <Route path="/calculadora-de-ganancias" element={<CalculadoraGananciasPage />} />
          <Route path="/centro-de-recursos" element={<CentroRecursosPage />} />
          <Route path="/planes-de-proteccion" element={<PlanesProteccionPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/host/seguro" element={<HostInsurancePage />} />
          <Route path="/host/payouts" element={<HostPayoutsPage />} />

          {/* Protected Guest Routes */}
          <Route
            path="/checkout/:vehicleId"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips"
            element={
              <ProtectedRoute>
                <TripsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips/:bookingId"
            element={
              <ProtectedRoute>
                <TripDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seguridad"
            element={
              <ProtectedRoute>
                <SecurityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <InboxPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inbox/:bookingId"
            element={
              <ProtectedRoute>
                <ConversationPage />
              </ProtectedRoute>
            }
          />

          {/* Host Routes */}
          <Route
            path="/host/dashboard"
            element={
              <ProtectedRoute requiredRoles={["host", "admin"]}>
                <HostDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/host/listings"
            element={
              <ProtectedRoute requiredRoles={["host", "admin"]}>
                <HostListingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/host/calendar"
            element={
              <ProtectedRoute requiredRoles={["host", "admin"]}>
                <HostCalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/host/analytics"
            element={
              <ProtectedRoute requiredRoles={["host", "admin"]}>
                <HostAnalyticsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRoles={["admin"]}>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <AIChatWidget />
      <BottomNav />
      <CookieBanner />
      <ProfilePhotoGate />
      <AddToHomeScreenPrompt />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <PwaInstallProvider>
            <CompareProvider>
              <AppRouter />
              <PwaInstallSurfaces />
              <Toaster position="top-right" richColors />
            </CompareProvider>
          </PwaInstallProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
